import React, { useState, useEffect } from 'react';
import api from './api';
import { buildChampionshipConfig } from './utils/championshipFormat';
import './styles/index.css';

const CrearTorneo = ({ onBack, onTorneoCreado }) => {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipoBracket, setTipoBracket] = useState('8');
  const [tipoEliminacion, setTipoEliminacion] = useState('doble'); // 'doble' o 'simple'
  const [modalidad, setModalidad] = useState('individual'); // 'individual' o 'parejas'
  const [fechaInicio, setFechaInicio] = useState('');
  const [juegosParaGanar, setJuegosParaGanar] = useState(4);
  const [juegosParaGanarLoser, setJuegosParaGanarLoser] = useState(null); // null = igual que winner
  const [juegosParaGanarFinal, setJuegosParaGanarFinal] = useState(null); // null = igual que winner
  
  // Búsqueda de jugadores
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Para parejas
  const [equipos, setEquipos] = useState([]); // Array de { jugador1, jugador2 }
  const [jugadorPareja1, setJugadorPareja1] = useState(null); // Primer jugador de la pareja en creación
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Establecer fecha por defecto a hoy
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFechaInicio(today);
  }, []);

  // Buscar jugadores
  const searchPlayers = async () => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const data = await api.searchUsers(searchTerm, 'jugador');
      // Filtrar jugadores ya seleccionados (individual) o en equipos (parejas)
      let idsUsados = [];
      if (modalidad === 'parejas') {
        idsUsados = equipos.flatMap(e => [e.jugador1._id, e.jugador2._id]);
        if (jugadorPareja1) idsUsados.push(jugadorPareja1._id);
      } else {
        idsUsados = selectedPlayers.map(p => p._id);
      }
      const filtered = (data || []).filter(
        u => !idsUsados.includes(u._id)
      );
      setSearchResults(filtered.slice(0, 10));
    } catch (err) {
      console.error('Error buscando jugadores:', err);
    } finally {
      setSearching(false);
    }
  };

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchPlayers();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedPlayers, equipos, jugadorPareja1, modalidad]);

  // Limpiar selecciones al cambiar modalidad
  const cambiarModalidad = (nuevaModalidad) => {
    if (nuevaModalidad !== modalidad) {
      setSelectedPlayers([]);
      setEquipos([]);
      setJugadorPareja1(null);
      setSearchTerm('');
      setSearchResults([]);
      setModalidad(nuevaModalidad);
    }
  };

  const cambiarTipoEliminacion = (nuevoTipo) => {
    if (nuevoTipo === tipoEliminacion) return;

    setTipoEliminacion(nuevoTipo);

    if (nuevoTipo === 'simple') {
      setJuegosParaGanarLoser(null);
    } else {
      setJuegosParaGanarFinal(null);
    }
  };

  const addPlayer = (player) => {
    if (selectedPlayers.length >= parseInt(tipoBracket)) {
      setError(`Máximo ${tipoBracket} jugadores para este tipo de bracket`);
      return;
    }
    setSelectedPlayers([...selectedPlayers, player]);
    setSearchTerm('');
    setSearchResults([]);
    setError('');
  };

  // Añadir jugador a pareja
  const addPlayerPareja = (player) => {
    if (!jugadorPareja1) {
      // Primer jugador de la pareja
      setJugadorPareja1(player);
      setSearchTerm('');
      setSearchResults([]);
    } else {
      // Segundo jugador, completar pareja
      if (equipos.length >= parseInt(tipoBracket)) {
        setError(`Máximo ${tipoBracket} equipos para este tipo de bracket`);
        return;
      }
      setEquipos([...equipos, { jugador1: jugadorPareja1, jugador2: player }]);
      setJugadorPareja1(null);
      setSearchTerm('');
      setSearchResults([]);
      setError('');
    }
  };

  // Cancelar selección del primer jugador de pareja
  const cancelarJugadorPareja1 = () => {
    setJugadorPareja1(null);
    setSearchTerm('');
  };

  // Eliminar equipo
  const removeEquipo = (index) => {
    setEquipos(equipos.filter((_, i) => i !== index));
  };

  const removePlayer = (playerId) => {
    setSelectedPlayers(selectedPlayers.filter(p => p._id !== playerId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validaciones
    if (!nombre.trim()) {
      setError('El nombre del torneo es obligatorio');
      return;
    }

    if (modalidad === 'parejas') {
      // Validaciones para parejas
      if (equipos.length < 2) {
        setError('Se necesitan al menos 2 equipos (4 jugadores)');
        return;
      }
      if (equipos.length > parseInt(tipoBracket)) {
        setError(`Máximo ${tipoBracket} equipos para este tipo de bracket`);
        return;
      }
      if (jugadorPareja1) {
        setError('Tienes un jugador sin pareja. Completa el equipo o quítalo');
        return;
      }
    } else {
      // Validaciones para individual
      if (selectedPlayers.length < 2) {
        setError('Se necesitan al menos 2 jugadores');
        return;
      }
      if (selectedPlayers.length > parseInt(tipoBracket)) {
        setError(`Máximo ${tipoBracket} jugadores para este tipo de bracket`);
        return;
      }
    }

    try {
      setLoading(true);

      // Preparar body según modalidad
      const bodyData = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
        tipoBracket,
        tipoEliminacion, // 'doble' o 'simple'
        fechaInicio,
        modalidad,
        configuracion: buildChampionshipConfig({
          tipoEliminacion,
          juegosParaGanar,
          juegosParaGanarLoser,
          juegosParaGanarFinal,
        })
      };

      if (modalidad === 'parejas') {
        bodyData.equipos = equipos.map(eq => ({
          jugador1Id: eq.jugador1._id,
          jugador2Id: eq.jugador2._id
        }));
      } else {
        bodyData.participantesIds = selectedPlayers.map(p => p._id);
      }

      // 1. Crear el campeonato
      const data = await api.crearCampeonato(bodyData);

      setSuccess('Torneo creado. Generando bracket...');

      // 2. Generar el bracket automáticamente (usa el mismo algoritmo que la app móvil)
      const bracketData = await api.generarBracketCampeonato(data.campeonato._id);

      setSuccess(`¡Torneo "${nombre}" creado con bracket listo! 🎯`);
      
      // Esperar un momento y llamar callback
      setTimeout(() => {
        if (onTorneoCreado) {
          onTorneoCreado(bracketData.campeonato || data.campeonato);
        }
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    api.logout();
    window.location.reload();
  };

  return (
    <div className="dart-league-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="user-info">
          <img src="/Logo26.png" alt="501 Dart League" className="dashboard-logo" />
          <div>
            <h1>➕ Crear Torneo</h1>
            <p>Configura tu nuevo torneo</p>
          </div>
        </div>
        <div className="header-buttons">
          <button onClick={onBack} className="back-button">
            ← Volver
          </button>
          <button onClick={handleLogout} className="logout-button">
            Cerrar sesión
          </button>
        </div>
      </div>

      {/* Formulario */}
      <div className="crear-torneo-container">
        <form onSubmit={handleSubmit} className="crear-torneo-form">
          {error && <div className="error-banner">⚠️ {error}</div>}
          {success && <div className="success-banner">✅ {success}</div>}

          {/* Datos básicos */}
          <div className="form-section">
            <h3>📋 Información del Torneo</h3>
            
            <div className="form-group">
              <label>Nombre del Torneo *</label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Torneo de Primavera 2025"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Descripción</label>
              <textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Descripción opcional del torneo..."
                rows={3}
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Tipo de Bracket</label>
                <select
                  value={tipoBracket}
                  onChange={(e) => {
                    setTipoBracket(e.target.value);
                    // Limpiar jugadores/equipos si exceden el nuevo límite
                    const newLimit = parseInt(e.target.value);
                    if (selectedPlayers.length > newLimit) {
                      setSelectedPlayers(selectedPlayers.slice(0, newLimit));
                    }
                    if (equipos.length > newLimit) {
                      setEquipos(equipos.slice(0, newLimit));
                    }
                  }}
                  disabled={loading}
                >
                  <option value="8">{modalidad === 'parejas' ? '8 Equipos' : '8 Jugadores'}</option>
                  <option value="16">{modalidad === 'parejas' ? '16 Equipos' : '16 Jugadores'}</option>
                  <option value="32">{modalidad === 'parejas' ? '32 Equipos' : '32 Jugadores'}</option>
                  <option value="64">{modalidad === 'parejas' ? '64 Equipos' : '64 Jugadores'}</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Eliminación</label>
                <select
                  value={tipoEliminacion}
                  onChange={(e) => cambiarTipoEliminacion(e.target.value)}
                  disabled={loading}
                >
                  <option value="doble">🔄 Doble Eliminación</option>
                  <option value="simple">⚡ Eliminación Simple (Solo Winner)</option>
                </select>
                <small className="form-help">
                  {tipoEliminacion === 'simple' 
                    ? 'Los jugadores que pierden son eliminados directamente' 
                    : 'Los jugadores tienen una segunda oportunidad en el Loser Bracket'}
                </small>
              </div>

              <div className="form-group">
                <label>Fecha de Inicio</label>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Formato Partidas</label>
                <select
                  value={juegosParaGanar}
                  onChange={(e) => setJuegosParaGanar(parseInt(e.target.value))}
                  disabled={loading}
                >
                  <option value={2}>Best of 3</option>
                  <option value={3}>Best of 5</option>
                  <option value={4}>Best of 7</option>
                  <option value={5}>Best of 9</option>
                </select>
              </div>

              {tipoEliminacion === 'doble' && (
                <div className="form-group">
                  <label>Formato Loser Bracket</label>
                  <select
                    value={juegosParaGanarLoser === null ? '' : juegosParaGanarLoser}
                    onChange={(e) => setJuegosParaGanarLoser(e.target.value === '' ? null : parseInt(e.target.value))}
                    disabled={loading}
                  >
                    <option value="">Igual que Winner</option>
                    <option value={2}>Best of 3</option>
                    <option value={3}>Best of 5</option>
                    <option value={4}>Best of 7</option>
                    <option value={5}>Best of 9</option>
                  </select>
                </div>
              )}

              {tipoEliminacion === 'simple' && (
                <div className="form-group">
                  <label>Formato Final</label>
                  <select
                    value={juegosParaGanarFinal === null ? '' : juegosParaGanarFinal}
                    onChange={(e) => setJuegosParaGanarFinal(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                    disabled={loading}
                  >
                    <option value="">Igual que Winner</option>
                    <option value={2}>Best of 3</option>
                    <option value={3}>Best of 5</option>
                    <option value={4}>Best of 7</option>
                    <option value={5}>Best of 9</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Jugadores */}
          <div className="form-section">
            {/* Selector de modalidad */}
            <div className="modalidad-selector">
              <h3>🎯 Modalidad del Torneo</h3>
              <div className="modalidad-buttons">
                <button
                  type="button"
                  className={`modalidad-btn ${modalidad === 'individual' ? 'active' : ''}`}
                  onClick={() => cambiarModalidad('individual')}
                  disabled={loading}
                >
                  👤 Individual
                </button>
                <button
                  type="button"
                  className={`modalidad-btn ${modalidad === 'parejas' ? 'active' : ''}`}
                  onClick={() => cambiarModalidad('parejas')}
                  disabled={loading}
                >
                  👥 Parejas
                </button>
              </div>
              <small className="form-help">
                {modalidad === 'individual' 
                  ? 'Cada jugador compite de forma individual' 
                  : 'Equipos de 2 jugadores que compiten juntos'}
              </small>
            </div>

            {/* Contenido según modalidad */}
            {modalidad === 'individual' ? (
              /* MODO INDIVIDUAL */
              <>
                <h3>👥 Jugadores ({selectedPlayers.length}/{tipoBracket})</h3>

                {/* Buscador */}
                <div className="form-group search-group">
                  <label>Buscar Jugador</label>
                  <div className="search-input-container">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Escribe el nombre del jugador..."
                      disabled={loading || selectedPlayers.length >= parseInt(tipoBracket)}
                    />
                    {searching && <span className="search-spinner">⏳</span>}
                  </div>

                  {/* Resultados de búsqueda */}
                  {searchResults.length > 0 && (
                    <div className="search-results-dropdown">
                      {searchResults.map(player => (
                        <div 
                          key={player._id} 
                          className="search-result-item"
                          onClick={() => addPlayer(player)}
                        >
                          <span className="player-name">{player.username || player.nombre}</span>
                          <span className="add-btn">+ Añadir</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lista de jugadores seleccionados */}
                <div className="selected-players-list">
                  {selectedPlayers.length === 0 ? (
                    <p className="empty-players">No hay jugadores seleccionados</p>
                  ) : (
                    selectedPlayers.map((player, index) => (
                      <div key={player._id} className="selected-player-item">
                        <span className="player-number">{index + 1}</span>
                        <span className="player-name">{player.username || player.nombre}</span>
                        <button 
                          type="button"
                          className="remove-player-btn"
                          onClick={() => removePlayer(player._id)}
                          disabled={loading}
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              /* MODO PAREJAS */
              <>
                <h3>👥 Equipos ({equipos.length}/{tipoBracket})</h3>

                {/* Creador de parejas */}
                <div className="form-group search-group">
                  <label>
                    {jugadorPareja1 
                      ? `🔗 Buscando pareja para: ${jugadorPareja1.username || jugadorPareja1.nombre}`
                      : 'Buscar Jugador (1° del equipo)'}
                  </label>
                  <div className="search-input-container">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={jugadorPareja1 
                        ? "Busca el segundo jugador del equipo..." 
                        : "Busca el primer jugador del equipo..."}
                      disabled={loading || equipos.length >= parseInt(tipoBracket)}
                    />
                    {searching && <span className="search-spinner">⏳</span>}
                    {jugadorPareja1 && (
                      <button 
                        type="button" 
                        className="cancel-pareja-btn"
                        onClick={cancelarJugadorPareja1}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Resultados de búsqueda */}
                  {searchResults.length > 0 && (
                    <div className="search-results-dropdown">
                      {searchResults.map(player => (
                        <div 
                          key={player._id} 
                          className="search-result-item"
                          onClick={() => addPlayerPareja(player)}
                        >
                          <span className="player-name">{player.username || player.nombre}</span>
                          <span className="add-btn">
                            {jugadorPareja1 ? '+ Formar Equipo' : '+ Seleccionar'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lista de equipos creados */}
                <div className="equipos-list">
                  {equipos.length === 0 ? (
                    <p className="empty-players">No hay equipos creados</p>
                  ) : (
                    equipos.map((equipo, index) => (
                      <div key={index} className="equipo-item">
                        <span className="equipo-number">Equipo {index + 1}</span>
                        <span className="equipo-players">
                          {equipo.jugador1.username || equipo.jugador1.nombre}
                          {' / '}
                          {equipo.jugador2.username || equipo.jugador2.nombre}
                        </span>
                        <button 
                          type="button"
                          className="remove-player-btn"
                          onClick={() => removeEquipo(index)}
                          disabled={loading}
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Aviso si hay jugador pendiente */}
                {jugadorPareja1 && (
                  <div className="pareja-pendiente-warning">
                    ⚠️ Tienes seleccionado a <strong>{jugadorPareja1.username || jugadorPareja1.nombre}</strong>. 
                    Busca su compañero de equipo o cancela la selección.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Botón de envío */}
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading || (modalidad === 'individual' ? selectedPlayers.length < 2 : equipos.length < 2)}
            >
              {loading ? '⏳ Creando...' : '🏆 Crear Torneo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrearTorneo;
